/**
 * Status chip (docs/14): rectangular, 2px radius, icon + mono text, 10% fill of the status
 * colour from the fixed map in `status.ts`. Never colour alone.
 */
import { Ban, Check, Circle, Clock, Lock, Radio, TriangleAlert, type LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn';
import { toneOf, TONE_COLOR, type StatusTone } from '../status';

const ICONS: Record<StatusTone, LucideIcon> = {
  grey: Circle,
  cyan: Radio,
  purple: Lock,
  lime: Check,
  red: TriangleAlert,
  amber: Clock,
};

export type StatusChipProps = {
  status: string;
  /** Visible text (translated by the caller); defaults to the status with spaces. */
  label?: string;
  tone?: StatusTone;
  /** Override the tone's icon (e.g. a tier mark that is not a state). */
  icon?: LucideIcon;
  className?: string;
};

export function StatusChip({ status, label, tone, icon, className }: StatusChipProps) {
  const t = tone ?? toneOf(status);
  const color = TONE_COLOR[t];
  const Icon = icon ?? (status === 'cancelled' || status === 'blacklisted' ? Ban : ICONS[t]);
  return (
    <span
      data-status={status}
      data-tone={t}
      className={cn(
        'font-data inline-flex items-center gap-1 rounded-[2px] border px-1.5 py-0.5 text-[11px] font-medium uppercase leading-4 tracking-wide',
        className,
      )}
      style={{
        color,
        borderColor: `color-mix(in srgb, ${color} 35%, transparent)`,
        backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)`,
      }}
    >
      <Icon aria-hidden className="size-3" strokeWidth={2} />
      {label ?? status.replace(/_/g, ' ')}
    </span>
  );
}
